import React, { useState } from 'react';
import {
    Form,
    Input,
    Row,
    Col,
    Modal,
    notification,
    DatePicker,
    Button,
    Typography,
    Tabs,
    Tooltip,
    Flex,
    Select,
    Checkbox
} from "antd";
import { InfoCircleOutlined } from '@ant-design/icons';

import axiosInstance from "../../../utilities/axios";
import ConditionSwitcher from "./ApplicabilityLogic";
import AmountOrder from './AmountOrder/AmountOrder';

const { Title } = Typography;

export const DISCOUNT_TYPES = [
  {'value': 'everyday_low', 'label': 'Everyday Low', 'path': '/everyday-low'},
  {'value': 'subsidy', 'label': 'Subsidy', 'path': '/subsidies'},
  {'value': 'matrix', 'label': 'Matrix', 'path': '/matrix'},
  {'value': 'affinity', 'label': 'Affinity', 'path': '/affinity-partners'},
  {'value': 'marketing', 'label': 'Marketing', 'path': '/discount-reasons'},
];

const NewDicountModal = ({ onChange, onClose, visible, path, allowMultiple }) => {
  const [form] = Form.useForm();
  const allValues = Form.useWatch([], form);

  const [activeKey, setActiveKey] = useState("0");

  const hasProductPriceDiscount = allValues?.discounts?.some((discount) =>
    discount?.amounts?.some((amount) => amount?.valueType === "product-price")
  );

  const onSubmit = (values) => {
    axiosInstance
      .post("/discounts", { ...values, "discountType": path === "/discount-reasons" ? "marketing" : path === "/subsidies" ? "subsidy" : path === "/matrix" ? "matrix" : path === "/affinity-partners" ? "affinity" : "everyday_low" })
      .then(() => {
        onChange();
        form.resetFields();
        setActiveKey("0");
        onClose();
        notification.success({
          message: "Success",
          description: "Discount created successfully",
        });
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  };

  return (
    <Modal
      title={path === "/discount-reasons" ? "New Discount Reason" : path === "/subsidies" ? "New Subsidy" : path === "/matrix" ? "New Matrix Discount" : path === "/affinity-partners" ? "New Affinity Partner" : "New Everyday Low Discount"}
      open={visible}
      onCancel={() => {
        onClose();
        setActiveKey("0");
        form.resetFields();
      }}
      width={800}
      footer={[]}
      styles={{ content: { overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' } }}
    >
      <Form
        form={form}
        name="newDiscountForm"
        onFinish={onSubmit}
        layout="vertical"
        initialValues={{ discounts: [{ amounts: [{}] }] }}
      >
        <Form.Item
          label={path === "/discount-reasons" ? "Discount Reason" : path === "/subsidies" ? "Subsidy Name" : path === "/matrix" ? "Matrix Name" : path === "/affinity-partners" ? "Affinity Partner Name" : "Everyday Low Name"}
          name="discountName"
          rules={[{ required: true, message: "Please enter the discount name" }]}
        >
          <Input placeholder={path === "/discount-reasons" ? "Enter Discount Reason" : path === "/subsidies" ? "Enter Subsidy Name" : path === "/matrix" ? "Enter Matrix Name" : path === "/affinity-partners" ? "Enter Affinity Partner Name" : "Enter Everyday Low Name"} />
        </Form.Item>
        {hasProductPriceDiscount && (
          <Form.Item name="hideDiscountName" valuePropName="checked" preserve={false}>
            <Checkbox>Hide Product Price Discount Name on Estimates</Checkbox>
          </Form.Item>
        )}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Effective Date"
              name="effectiveDate"
              preserve={false}
              rules={[{ required: true, message: "Please input an effective date" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Expiration Date"
              name="expirationDate"
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="incompatibleDiscountTypes"
          label={
            <>
              Incompatible Discount Types
              <Tooltip title="Select the types of discounts that this discount cannot be combined with.">
                <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
              </Tooltip>
            </>
          }
        >
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select Incompatible Discount Types"
          >
            {DISCOUNT_TYPES.filter(type => type.path !== path).map((type) => (
              <Select.Option key={type.value} value={type.value}>
                {type.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Title level={5} style={{ marginTop: "0px" }}>Discounts</Title>
        <Form.List
          name="discounts"
          initialValue={[{}]}
        >
          {(fields, { add, remove }) => {
            const canRemove = fields.length > 1;
            return (
              <>
                <Tabs
                  type={allowMultiple ? "editable-card" : "card"}
                  activeKey={activeKey}
                  onChange={setActiveKey}
                  onEdit={(targetKey, action) => {
                    if (action === 'add') {
                      const newKey = `${fields.length}`;
                      add({ amounts: [{}] });
                      setActiveKey(newKey);
                    } else if (action === 'remove' && canRemove) {
                      remove(Number(targetKey));
                      setActiveKey(fields.length > 1 ? `${fields[0].name}` : "0");
                    }
                  }}
                  items={fields.map(({ key, name, ...restField }, idx) => {
                      return {
                      label: `Discount ${idx + 1}`,
                      key: `${name}`,
                      closable: canRemove,
                      children: (
                        <>
                          <Form.Item
                            {...restField}
                            label="Sub Discount Name"
                            name={[name, 'subDiscountName']}
                            rules={[{ required: true, message: "Please input a sub discount name" }]}
                          >
                            <Input placeholder="Enter Sub Discount Name" />
                          </Form.Item>

                          <Flex style={{ marginBottom: 9 }} align="center">
                            <Title level={5} style={{ margin: 0 }}>
                              Discount Amounts
                            </Title>
                            <Tooltip title="You can add multiple discount amounts for each discount. The order of these discount amounts will determine the order in which they are applied.">
                              <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                            </Tooltip>
                          </Flex>
                          <AmountOrder key={name} form={form} path={path} outerName={name} />

                          <Title level={5} style={{ marginTop: "24px" }}>
                            Applicability Rules
                          </Title>
                          <Form.Item name={[name, "logic"]} style={{ marginBottom: 24 }}>
                            <ConditionSwitcher />
                          </Form.Item>
                        </>
                      ),
                      };
                  })}
                />
              </>
            );
          }}
        </Form.List>
        <Button
          type="primary"
          htmlType="submit"
          block
        >
          {path === "/discount-reasons" ? "Add Discount Reason" : path === "/subsidies" ? "Add Subsidy" : path === "/matrix" ? "Add Matrix Discount" : path === "/affinity-partners" ? "Add Affinity Partner" : "Add Everyday Low Discount"}
        </Button>
      </Form>
    </Modal>
  )
};

export default NewDicountModal;